import React, { useCallback, useMemo, useState } from "react";
import { Category } from "@common/types/category";
import { useCategoriesTree } from "@hooks/useCategoriesTree";
import { pruneItemsByQuery } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { FilterPickerList } from "@components/pageHeader/filters/FilterPickerList/FilterPickerList";
import {
    applyCategoryToggle,
    collectItemValues,
} from "@components/pageHeader/filters/FilterPickerList/filterPickerTree";
import { isCategoryMatchingSearch } from "@components/settings/Categories/utils";
import { CategoryIcon } from "@components/transactions/CategoryIcon";
import { BaseFilterProps } from "../../lib";
import css from "./AggregatedCategoryFilter.module.scss";

interface AggregatedCategoryFilterProps extends BaseFilterProps {
    onlyStandardCategories?: boolean;
}

const categoryMatchesSearch = (category: Category, search: string) =>
    category.label.toLowerCase().includes(search.toLowerCase()) ||
    category.description?.toLowerCase().includes(search.toLowerCase()) ||
    isCategoryMatchingSearch(category, search);

export const AggregatedCategoryFilter: React.FC<
    AggregatedCategoryFilterProps
> = ({ filters, onChange, onlyStandardCategories = false }) => {
    const [query, setQuery] = useState("");
    const categories = useCategoriesTree({ onlyStandardCategories });

    const items = useMemo(
        (): Array<PickerItemData<string>> =>
            categories.map((category) => ({
                value: category.id,
                label: category.label,
                leading: <CategoryIcon categoryId={category.id} />,
                children: (category.subcategories ?? []).map((subcategory) => ({
                    value: subcategory.id,
                    label: subcategory.label,
                })),
            })),
        [categories],
    );
    const categoriesById = useMemo(
        () =>
            new Map(
                categories
                    .flatMap((category) => [
                        category,
                        ...(category.subcategories ?? []),
                    ])
                    .map((category) => [category.id, category]),
            ),
        [categories],
    );

    const matchesSearch = useCallback(
        (item: PickerItemData<string>, search: string) => {
            const category = categoriesById.get(item.value);
            return (
                category !== undefined &&
                categoryMatchesSearch(category, search)
            );
        },
        [categoriesById],
    );

    const selectedCategoryIds = useMemo(
        () => filters.category ?? [],
        [filters.category],
    );

    const handleValueChange = useCallback(
        (nextIds: string[]) => {
            const nextSelectedIds = applyCategoryToggle({
                selectedIds: selectedCategoryIds,
                nextIds,
                categoriesById,
            });
            if (nextSelectedIds === undefined) {
                return;
            }
            onChange({
                category:
                    nextSelectedIds.length > 0 ? nextSelectedIds : undefined,
            });
        },
        [categoriesById, onChange, selectedCategoryIds],
    );

    const reverseSelection = useCallback(() => {
        const selectedSet = new Set(selectedCategoryIds);
        onChange({
            category: collectItemValues(
                pruneItemsByQuery(items, query, matchesSearch),
            ).filter((id) => !selectedSet.has(id)),
        });
    }, [items, matchesSearch, onChange, query, selectedCategoryIds]);

    return (
        <div className={css.filter}>
            <FilterPickerList
                items={items}
                value={selectedCategoryIds}
                onValueChange={handleValueChange}
                filter={matchesSearch}
                onInputValueChange={setQuery}
                emptyLabel="No matching categories"
                onDeselect={() => onChange({ category: undefined })}
                onReverse={reverseSelection}
            />
        </div>
    );
};
